import { EINVOICE_MESSAGES } from '@palaccount/contracts';
import { AlertTriangle, ExternalLink, RefreshCw, Send, ShieldCheck, ShieldQuestion, Verified } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount, Ltr, Pct } from '@/components/amount';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useInvoiceCompliance,
  useResubmitCompliance,
  useSendToCustomer,
} from '@/features/einvoicing/api';
import { STATUS_META, TAX_CATEGORY_META, gateBanner, sendable } from '@/features/einvoicing/compliance';
import { localizeServerCause, localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

/**
 * SAL-04 §5 — the E-invoicing & compliance strip, "which is what makes a
 * sales invoice different from a purchase order".
 *
 * It sits between the details line and the item grid (§4), and everything on
 * it renders for every user who can open the screen (§9, ruling R1 — there
 * is one interface). Two things vary and neither is a mode:
 *   - PERMISSION: Resubmit and Send are gated; a user without the right
 *     reads the strip and cannot act on it.
 *   - JURISDICTION: whether the clearance banners appear at all comes from
 *     the company's configured adapter, an organization setting.
 *
 * BR-SAL-04-05 is the rule that shapes the file: the submission status is
 * READ from the E-Invoicing module and never copied onto the invoice, "so
 * the two can never disagree". There is no compliance column on `invoices`,
 * and there never will be.
 *
 * Two prototype defects fixed here:
 *   - BR-SAL-04-07: "the line-level tax table must list the invoice's own
 *     lines. In the prototype it renders empty whenever no submission record
 *     exists." The server derives the breakdown from the invoice, so a
 *     Pending record draws a full table.
 *   - Ruling R1: Resubmit is never drawn without the reason beside it.
 *
 * ═══ SAL-04 RULES THIS FILE DELIBERATELY DOES NOT CITE, AND WHY ══════════
 * Ignore-marked per line, so this paragraph names nothing.
 *
 *   BR-SAL-04-02 — rule-coverage:ignore — "under the reporting model,
 *     neither clearance banner appears and sending is not blocked." IT IS
 *     ENFORCED, and now cited at both enforcing lines (sal04-rules wave,
 *     which is the "whoever owns those two files" an earlier note left it
 *     for): the banner half at `gateBanner`'s reporting return in
 *     `features/einvoicing/compliance.ts`, the send half at the clearance
 *     gate in `apps/api/src/einvoicing/einvoicing.service.ts`, and the
 *     server clause pinned by the reporting-invoice-still-PENDING send in
 *     `einvoicing.e2e.spec.ts`. It stays uncited HERE because this file only
 *     draws `gateBanner`'s verdict — naming the rule where the banner is
 *     rendered rather than where it is decided is the drift the ignore mark
 *     refuses.
 *
 *   The invoice-cancel pair — rule-coverage:ignore — that used to sit here
 *     as NOT ENFORCED is enforced now, and the note is kept rather than
 *     deleted because HOW it stopped being true is the useful part. It read:
 *     "a SALES INVOICE HAS NO CANCEL PATH AT ALL … the rule's trigger cannot
 *     occur; there is nothing to add a cancellation submission to. Building
 *     the cancel act is a product decision … and it is not this file's."
 *     That was exactly right, the product decision was taken as delegated
 *     ruling A6, and the two rules are now cited at the lines that enforce
 *     them — the raise in `apps/api/src/invoices/invoices.service.ts` and the
 *     dialog sentence in `invoice-editor-page.tsx`. They are still not cited
 *     HERE, for the reason the paragraph above gives about BR-SAL-04-02: this
 *     strip draws a verdict it does not decide, and naming a rule where its
 *     effect is visible rather than where it is enforced is the drift the
 *     ignore mark refuses.
 */
export function ComplianceStrip({
  invoiceId,
  posted,
}: {
  invoiceId: string;
  posted: boolean;
}) {
  const { t } = useTranslation();
  const { data: record, isPending } = useInvoiceCompliance(posted ? invoiceId : undefined);
  const resubmit = useResubmitCompliance();
  const send = useSendToCustomer();
  const [error, setError] = useState<string | null>(null);

  // A DRAFT has filed nothing and owes nothing: the compliance record is
  // created when the invoice is issued (Q-FIN-08-5), so before posting the
  // strip has nothing true to say and stays away.
  if (!posted) return null;
  if (isPending) return null;

  // §5a field 2: "when no submission exists the chip falls back to Pending".
  const status = record?.status ?? 'pending';
  const banner = record ? gateBanner(record) : null;

  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      await action();
    } catch (caught) {
      // Its own key, not the e-invoicing screen's, even though the two carry
      // the same words today — this strip is a different screen and may need a
      // different sentence without the other one having to be untangled first.
      setError(localizeServerCause(t, caught, 'einv.errSaveFailedStrip'));
    }
  };

  return (
    <Card data-testid="compliance-strip">
      <CardContent className="grid gap-3">
        {/* ── 5a. The strip header ─────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-3">
          <Verified className="size-4 text-emerald-700 dark:text-emerald-400" aria-hidden />
          <h3 className="font-semibold">{t('sales.stripTitle')}</h3>
          <span
            className={cn(
              'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold',
              STATUS_META[status].chip,
            )}
          >
            {t(STATUS_META[status].labelKey)}
          </span>
          <div className="ms-auto flex flex-wrap gap-2">
            {/* §5a field 4 — Resubmit appears ONLY when rejected, and the
                reason is right beside it in the banner below (ruling R1). */}
            {record && status === 'rejected' ? (
              <Gated permission="sales.approve" reasonKey="perm.cannot.submitEinvoices">
                <Button
                  variant="destructive"
                  disabled={resubmit.isPending}
                  onClick={() => void run(() => resubmit.mutateAsync(record.id))}
                >
                  <RefreshCw aria-hidden /> {t('einv.resubmit')}
                </Button>
              </Gated>
            ) : null}
            {/* BR-SAL-04-01's gate, as an action rather than as advice. Send
                stays DISABLED-but-live when the record is not yet sendable —
                that is the tax authority's clearance, a rule and not a
                permission, and 16.7 row 3 keeps the two apart. */}
            {record ? (
              <Gated permission="sales.edit" reasonKey="perm.cannot.sendInvoices">
                <Button
                  variant="outline"
                  disabled={!sendable(record) || send.isPending}
                  onClick={() => void run(() => send.mutateAsync(invoiceId))}
                >
                  <Send aria-hidden />
                  {record.sentToCustomerAt === null ? t('sales.sendToCustomer') : t('sales.sentToCustomer')}
                </Button>
              </Gated>
            ) : null}
            {/* §5a field 5 — Open in E-Invoicing, with this invoice selected. */}
            <Button asChild variant="outline">
              <Link
                to={`/accounting/einvoicing?tab=invoices${record ? `&submission=${record.id}` : ''}`}
              >
                <ExternalLink aria-hidden /> {t('sales.openInEinvoicing')}
              </Link>
            </Button>
          </div>
        </div>

        {error ? (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        {/* ── 5b. The three gate banners — exactly one at a time ───────── */}
        {banner === 'clearance_blocked' ? (
          <p className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            <ShieldQuestion className="size-4 shrink-0" aria-hidden />
            {t('sales.stripClearanceBlocked')}
          </p>
        ) : null}
        {banner === 'cleared' ? (
          <p className="flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
            <ShieldCheck className="size-4 shrink-0" aria-hidden />
            {t('sales.stripCleared')}
          </p>
        ) : null}
        {/* ERR-SAL-04-02 — "Submission rejected:" followed by the authority's
            reason, and BR-SAL-04-03's first clause: "a rejected submission
            must show the authority's reason". The reason is the SERVER's
            stored `rejectionReason`, never a generic sentence, and the banner
            outranks both clearance banners so it cannot be hidden behind them.

            BR-SAL-04-03's second clause — "and must not block payment. The
            invoice stays a valid receivable while the rejection is fixed" —
            is satisfied structurally rather than by a check: NOTHING outside
            `einvoicing` reads `einvoice_submissions`. The receipt path
            (`receipts.service.ts`) selects the customer's open invoices on
            `status = 'posted'` and the unpaid balance alone, so a rejection
            is not a fact it can see. Safe by construction, not by margin. */}
        {banner === 'rejected' && record ? (
          <p className="flex flex-wrap items-center gap-2 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
            <AlertTriangle className="size-4 shrink-0" aria-hidden />
            <strong>{t('sales.stripRejected')}</strong>
            <span>{record.rejectionReason}</span>
            {/* §5c field 9 — the offending field, in red fixed-width. */}
            <code className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-xs dark:bg-red-900">
              <Ltr value={record.rejectedField ?? ''} />
            </code>
          </p>
        ) : null}

        {/* ── 5c. The compliance detail: QR panel + identifier column ──── */}
        {record ? (
          <div className="grid gap-4 sm:grid-cols-[140px_1fr]">
            <div className="grid gap-1">
              <div className="flex size-[130px] items-center justify-center rounded-md border bg-white p-2">
                {record.qrPayload ? (
                  <StripQr payload={record.qrPayload} />
                ) : (
                  <span className="text-center text-xs text-muted-foreground">{t('einv.noQr')}</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {localizeServerMessage(t, EINVOICE_MESSAGES.qrPlaceholderNote)}
              </p>
            </div>
            <dl className="grid gap-1.5 text-sm">
              <StripField label={t('einv.fUuid')} value={record.uuid} />
              <StripField label={t('einv.fAuthorityCode')} value={record.authorityCode} />
              <StripField label={t('einv.fFormat')} value={record.format} />
              <StripField
                label={t('einv.fModel')}
                value={t(`einv.model_${record.submissionModel}`)}
                plain
              />
            </dl>
          </div>
        ) : null}

        {/* ── 5d. The line-level tax table — the invoice's OWN lines ───── */}
        {record ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('einv.colDescription')}</TableHead>
                <TableHead>{t('einv.colRate')}</TableHead>
                <TableHead>{t('einv.colCategory')}</TableHead>
                <TableHead className="text-end">{t('einv.colNet')}</TableHead>
                <TableHead className="text-end">{t('einv.colTax')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {record.taxBreakdown.lines.map((line) => (
                <TableRow key={line.lineNo}>
                  <TableCell>{line.description}</TableCell>
                  <TableCell className="font-mono">
                    <Pct value={line.taxRate} />
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                        TAX_CATEGORY_META[line.taxCategory].chip,
                      )}
                    >
                      {t(TAX_CATEGORY_META[line.taxCategory].labelKey)}
                    </span>
                  </TableCell>
                  <TableCell className="text-end">
                    <Amount value={line.net} currency={record.currency} />
                  </TableCell>
                  <TableCell className="text-end font-semibold">
                    <Amount value={line.tax} currency={record.currency} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            {/*
             * NO TOTALS FOOTER, deliberately.
             *
             * SAL-04 §5d specifies this table as five columns — Description,
             * Rate, Tax category, Net, Tax — and §4's layout draws it with no
             * footer row. The "must total net, tax and gross" rule is
             * BR-FIN-08-13, a FIN-08 rule, and "the footer promises Net / Tax
             * total / Gross but shows only two figures" is in FIN-08's defects
             * list, not SAL-04's. The totals live on the compliance screen,
             * where the spec puts them, and they are still there.
             *
             * On THIS screen a totals row is also actively wrong: SAL-00 §5f's
             * own totals box sits two cards below, and a second grand total
             * beside it is one more number a reader has to reconcile for no
             * gain. Repeating a figure is not the same as reporting it.
             */}
          </Table>
        ) : null}
      </CardContent>
    </Card>
  );
}

function StripField({
  label,
  value,
  plain,
}: {
  label: string;
  value: string | null;
  plain?: boolean | undefined;
}) {
  return (
    <div className="grid grid-cols-[9rem_1fr] items-baseline gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn('break-all', !plain && 'font-mono text-xs')}>
        {value === null || value === '' ? '—' : <Ltr value={value} />}
      </dd>
    </div>
  );
}

/**
 * The same 12×12 placeholder tile as FIN-08's, over the STORED payload.
 *
 * ERR-SAL-04-03 / ERR-SAL-04-06 are the two halves of one prototype defect —
 * the tile LOOKS like a QR code and does not scan. It is still a placeholder
 * here and deliberately so (BR-SAL-04-06: the encoding is jurisdiction-specific
 * and no jurisdiction adapter is built). What changed is that the caption
 * beneath it says so, so nobody reaches for a phone.
 */
function StripQr({ payload }: { payload: string }) {
  let hash = 2166136261;
  const cells: boolean[] = [];
  for (let index = 0; index < 144; index += 1) {
    hash ^= payload.charCodeAt(index % payload.length);
    hash = Math.imul(hash, 16777619) >>> 0;
    cells.push((hash & 0x40) !== 0);
  }
  return (
    <div className="grid size-full grid-cols-12 gap-0" aria-hidden>
      {cells.map((on, index) => (
        <span key={index} className={on ? 'bg-black' : 'bg-white'} />
      ))}
    </div>
  );
}
